import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Button, Input, Popconfirm, Space, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { useToast } from '../contexts/ToastContext'
import { useDeleteRuleSetMutation, useRuleSets } from '../hooks/useFileUploadRules'
import type { RuleSet } from '../types/fileUploadRules'
import { severityCounts } from '../features/fileUploadRules/ruleConfig'
import RuleSetEditorDrawer from '../features/fileUploadRules/RuleSetEditorDrawer'

function formatDate(value?: string) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString()
}

export default function FileUploadRules() {
  const { addToast } = useToast()
  const ruleSetsQuery = useRuleSets()
  const deleteMutation = useDeleteRuleSetMutation()

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [search, setSearch] = useState(searchParams.get('table') ?? '')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingTableName, setEditingTableName] = useState<string | null>(null)

  const rows = useMemo(() => ruleSetsQuery.data ?? [], [ruleSetsQuery.data])
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((row) => row.tableName.toLowerCase().includes(q))
  }, [rows, search])

  const existingTableNames = useMemo(() => rows.map((row) => row.tableName), [rows])

  const openCreate = () => {
    setEditingTableName(null)
    setDrawerOpen(true)
  }

  const openEdit = (tableName: string) => {
    setEditingTableName(tableName)
    setDrawerOpen(true)
  }

  const handleDelete = async (tableName: string) => {
    try {
      await deleteMutation.mutateAsync(tableName)
      addToast('Rule set deleted.', 'success')
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to delete rule set', 'error')
    }
  }

  const columns: ColumnsType<RuleSet> = [
    { title: 'Table', dataIndex: 'tableName', key: 'tableName', render: (value: string) => <span className="font-medium">{value}</span> },
    {
      title: 'Rules',
      key: 'rules',
      render: (_value, row) => row.rules.length,
    },
    {
      title: 'Severities',
      key: 'severities',
      render: (_value, row) => {
        const counts = severityCounts(row.rules)
        return (
          <Space size={6}>
            {counts.error > 0 && <Tag color="red">{counts.error} Error</Tag>}
            {counts.warning > 0 && <Tag color="gold">{counts.warning} Warning</Tag>}
            {counts.error === 0 && counts.warning === 0 && <span className="text-gray-400">—</span>}
          </Space>
        )
      },
    },
    {
      title: 'Set Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value: boolean) => (value ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>),
    },
    {
      title: 'Updated',
      key: 'updated',
      render: (_value, row) => <span className="text-gray-500">{formatDate(row.updatedAt || row.createdAt)}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_value, row) => (
        <Space>
          <Tooltip title="Edit set">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row.tableName)}>
              Edit set
            </Button>
          </Tooltip>
          <Popconfirm
            title="Delete rule set?"
            description="This hard-deletes the entire set for this table and cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
            onConfirm={() => handleDelete(row.tableName)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Delete set
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="relative overflow-hidden rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white via-white to-amber-50/35">
      <div className="px-6 pt-5 pb-4 border-b border-brand-100">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Tooltip title="Back">
                <Button icon={<ArrowLeftOutlined />} aria-label="Back" onClick={() => navigate(-1)} />
              </Tooltip>
              <h1 className="font-display text-2xl font-bold text-gray-900 tracking-tight">File Upload Rules</h1>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              One rule set per table, applied to files during upload. <span className="text-red-600 font-medium">Error</span> blocks the upload;{' '}
              <span className="text-amber-600 font-medium">Warning</span> is surfaced only.
            </p>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Rule Set
          </Button>
        </div>
      </div>

      <div className="p-4">
        <Space wrap size={12} className="mb-3">
          <Input
            placeholder="Search table"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 260 }}
          />
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              aria-label="Refresh rule sets"
              onClick={() => void ruleSetsQuery.refetch()}
              loading={ruleSetsQuery.isFetching}
            />
          </Tooltip>
        </Space>

        {ruleSetsQuery.isError && (
          <Alert
            type="error"
            showIcon
            className="mb-3"
            message={(ruleSetsQuery.error as Error)?.message || 'Failed to load rule sets'}
          />
        )}

        <Table<RuleSet>
          rowKey="tableName"
          loading={ruleSetsQuery.isLoading}
          columns={columns}
          dataSource={filteredRows}
          pagination={false}
        />
      </div>

      <RuleSetEditorDrawer
        open={drawerOpen}
        editingTableName={editingTableName}
        existingTableNames={existingTableNames}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => void ruleSetsQuery.refetch()}
      />
    </div>
  )
}
